import React, { useState, useEffect, useRef } from 'react';
import { isTokenExpired } from '../utils/jwtUtils';
import QrScanner from 'react-qr-barcode-scanner';
import './Driver.css';
import { vehicleApi, gatemanApi } from '../api/adminapi';
import { saveUserLog } from '../api/driver';
import ToastContainer from '../Components/ToastContainer';
import { toast } from 'react-toastify';

const Driver = () => {
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [parsedQRData, setParsedQRData] = useState(null);
  const [assignedVehicleFromQR, setAssignedVehicleFromQR] = useState('');
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedRound, setSelectedRound] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [isVehiclesLoading, setIsVehiclesLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [isRoutesLoading, setIsRoutesLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [scannedUsers, setScannedUsers] = useState([]); // Array to store multiple scanned users
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scannerRef = useRef(null);
  const lastScanRef = useRef('');
  const autoSubmitTimeoutRef = useRef(null);


  useEffect(() => {
    const rawToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token');
    const token = rawToken ? rawToken.trim().replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '') : null;
    const userPhoneNumber = localStorage.getItem('userPhoneNumber');
    const userEmail = localStorage.getItem('userEmail');

    if (!token || isTokenExpired(token) || (!userPhoneNumber && !userEmail)) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('token');
      window.location.href = '/signin';
      return;
    }

    setIsOtpVerified(true);

    // Periodically check for expiry while on the page
    const intervalId = setInterval(() => {
      const currentRaw = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token');
      const current = currentRaw ? currentRaw.trim().replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '') : null;
      if (!current || isTokenExpired(current)) {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        localStorage.removeItem('token');
        window.location.href = '/signin';
      }
    }, 15000);

    // React to token changes from other tabs/windows
    const handleStorage = (e) => {
      if (e.key === 'authToken' || e.key === 'token') {
        const newTokenRaw = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token');
        const newToken = newTokenRaw ? newTokenRaw.trim().replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '') : null;
        if (!newToken || isTokenExpired(newToken)) {
          window.location.href = '/signin';
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Auto-control scanner based on selections
  useEffect(() => {
    if (!isOtpVerified) return;
    const hasSelections = Boolean(selectedVehicle) && Boolean(selectedRound);
    if (hasSelections && !scannerActive) {
      startScanner();
    } else if (!hasSelections && scannerActive) {
      stopScanner();
    }
  }, [isOtpVerified, selectedVehicle, selectedRound, scannerActive]);

  // Validate vehicle match when selected vehicle or assigned vehicle changes
  useEffect(() => {
    if (assignedVehicleFromQR && selectedVehicle) {
      if (assignedVehicleFromQR === selectedVehicle) {
        setError(''); // Clear any previous errors
        setDebugInfo(`✓ Vehicle match confirmed: ${assignedVehicleFromQR}`);

        // Show success notification for vehicle match
        toast.success(`✓ Vehicle match confirmed: ${assignedVehicleFromQR}`, {
          position: "top-right",
          autoClose: 2500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        setError(''); // Clear any previous errors
        setDebugInfo(`QR shows assigned vehicle: ${assignedVehicleFromQR}, Selected: ${selectedVehicle}`);

        // Show warning notification for vehicle mismatch
        toast.warning(`⚠ Vehicle mismatch! Assigned: ${assignedVehicleFromQR}, Selected: ${selectedVehicle}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } else if (assignedVehicleFromQR && !selectedVehicle) {
      setError(''); // Clear errors when no vehicle is selected
      setDebugInfo(`QR shows assigned vehicle: ${assignedVehicleFromQR}. Please select a vehicle to compare.`);
    }
  }, [selectedVehicle, assignedVehicleFromQR]);

  // Load vehicles after authentication is verified
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        setIsVehiclesLoading(true);
        setError('');
        const data = await vehicleApi.getAllVehicles();
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data?.data)) list = data.data;
        else if (Array.isArray(data?.content)) list = data.content;
        else list = [];
        setVehicles(list);

        // Show success notification
        if (list.length > 0) {
          toast.success(`✓ Loaded ${list.length} vehicles successfully`, {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } catch (e) {
        const errorMsg = e.response?.data?.message || e.message || 'Failed to fetch vehicles';
        setError(errorMsg);

        // Show error notification
        toast.error(`Failed to load vehicles: ${errorMsg}`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setIsVehiclesLoading(false);
      }
    };
    if (isOtpVerified) {
      loadVehicles();
    }
  }, [isOtpVerified]);

  // Load routes when vehicle is selected
  useEffect(() => {
    const loadRoutes = async () => {
      if (!selectedVehicle) {
        setRoutes([]);
        setSelectedRound('');
        return;
      }

      try {
        setIsRoutesLoading(true);
        const response = await gatemanApi.getRoutesByVehicle(selectedVehicle);

        // Handle response - could be array directly or wrapped in data property
        let routeList = [];
        if (Array.isArray(response)) {
          routeList = response;
        } else if (response?.data && Array.isArray(response.data)) {
          routeList = response.data;
        } else {
          routeList = [];
        }

        setRoutes(routeList);
        setSelectedRound(''); // Reset route selection when vehicle changes

        if (routeList.length > 0) {
          toast.success(`Found ${routeList.length} route(s) for this vehicle`, {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.warning('No routes assigned to this vehicle', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } catch (e) {
        const errorMsg = e.response?.data?.message || e.message || 'Failed to fetch routes';
        toast.error(`Failed to load routes: ${errorMsg}`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setRoutes([]);
      } finally {
        setIsRoutesLoading(false);
      }
    };

    const rawToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token');
    const token = rawToken ? rawToken.trim().replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '') : null;

    if (token && !isTokenExpired(token) && selectedVehicle) {
      loadRoutes();
    }
  }, [selectedVehicle]);

  // No special cleanup required for react-qr-barcode-scanner
  useEffect(() => {
    return () => {};
  }, []);

  const handleScan = async (decodedText, decodedResult) => {
    console.log('QR Code scanned:', decodedText);

    // Prevent duplicate scans within 2 seconds
    if (decodedText && decodedText === lastScanRef.current) {
      console.log('Duplicate scan detected, ignoring...');
      return;
    }

    if (decodedText && !isProcessing) {
      setIsProcessing(true);
      lastScanRef.current = decodedText;

      setScannedData(decodedText);
      setError('');

      // Try to parse the JSON data from the QR code
      try {
        const parsedData = JSON.parse(decodedText);
        setParsedQRData(parsedData);

        // Extract assigned vehicle from the parsed data
        const assignedVehicle = parsedData.assignedVehicle || parsedData.AssignedVehicle || '';
        setAssignedVehicleFromQR(assignedVehicle);

        console.log('Parsed QR Data:', parsedData);
        console.log('Assigned Vehicle from QR:', assignedVehicle);

        // Check if user is active
        const isActive = parsedData.status?.toUpperCase() === 'ACTIVE';

        if (isActive) {
          // Add to scanned users list instead of auto-submitting
          addToScannedUsers(parsedData, assignedVehicle);
        } else {
          // Show confirmation modal for inactive users
          setConfirmationData(parsedData);
          setShowConfirmation(true);
          setIsProcessing(false);
        }

      } catch (parseError) {
        console.log('Failed to parse QR data as JSON:', parseError);
        setParsedQRData(null);
        setAssignedVehicleFromQR('');
        setDebugInfo('QR data is not in JSON format');

        // Show error notification for invalid QR
        toast.error('Invalid QR code format. Please scan a valid employee QR code.', {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setIsProcessing(false);

        // Reset for next scan after 2 seconds
        setTimeout(() => {
          lastScanRef.current = '';
        }, 2000);
      }
    }
  };

  // Function to add scanned user to the list
  const addToScannedUsers = (parsedData, assignedVehicle) => {
    const empId = parsedData.empId || parsedData.EmpId || '';

    // Check if user is already in the list
    const alreadyScanned = scannedUsers.some(user => user.empId === empId);

    if (alreadyScanned) {
      toast.warning(`${parsedData.firstName} ${parsedData.secondName} is already in the list`, {
        position: "top-right",
        autoClose: 2500,
      });
      setIsProcessing(false);
      setTimeout(() => {
        lastScanRef.current = '';
      }, 2000);
      return;
    }

    // Find the selected route to get timing and route details
    const selectedRouteObj = routes.find(r => r.round === selectedRound);
    const timing = selectedRouteObj?.timing || '';
    const routeInfo = selectedRouteObj ? `${selectedRouteObj.source} → ${selectedRouteObj.destination}` : '';

    const userLog = {
      empId: empId,
      firstName: parsedData.firstName || '',
      secondName: parsedData.secondName || '',
      assignedVehicle: assignedVehicle || parsedData.assignedVehicle || parsedData.AssignedVehicle || '',
      travelingVehicle: selectedVehicle,
      round: selectedRound,
      timing: timing,
      route: routeInfo,
      status: parsedData.status || '',
      scannedAt: new Date().toLocaleTimeString(),
      vehicleMatch: assignedVehicle === selectedVehicle
    };

    setScannedUsers(prev => [...prev, userLog]);

    const userName = `${parsedData.firstName || ''} ${parsedData.secondName || ''}`.trim() || empId;
    toast.success(`${userName} added to list`, {
      position: "top-right",
      autoClose: 2000,
    });

    // Reset for next scan after a brief delay
    setTimeout(() => {
      setParsedQRData(null);
      setScannedData('');
      setAssignedVehicleFromQR('');
      setIsProcessing(false);
      lastScanRef.current = '';
    }, 1000);
  };

  // Function to remove a user from the scanned list
  const removeFromScannedUsers = (empId) => {
    setScannedUsers(prev => prev.filter(user => user.empId !== empId));
    toast.info('User removed from list', {
      position: "top-right",
      autoClose: 1500,
    });
  };

  // Function to clear all scanned users
  const clearAllScannedUsers = () => {
    setScannedUsers([]);
    toast.info('All users cleared from list', {
      position: "top-right",
      autoClose: 1500,
    });
  };

  // Function to submit all scanned users
  const submitAllUsers = async () => {
    if (scannedUsers.length === 0) {
      toast.warning('No users to submit. Please scan some QR codes first.', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const user of scannedUsers) {
      try {
        const payload = {
          empId: user.empId,
          assignedVehicle: user.assignedVehicle,
          travelingVehicle: user.travelingVehicle,
          round: user.round,
          timing: user.timing,
          route: user.route,
        };

        await saveUserLog(payload);
        successCount++;
      } catch (e) {
        failCount++;
        const msg = e?.response?.data?.message || e?.message || 'Failed';
        errors.push(`${user.firstName} ${user.secondName}: ${msg}`);
      }
    }

    setIsSubmitting(false);

    if (successCount > 0 && failCount === 0) {
      toast.success(`Successfully submitted ${successCount} user log(s)`, {
        position: "top-right",
        autoClose: 3000,
      });
      setScannedUsers([]); // Clear the list after successful submission
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`Submitted ${successCount}, failed ${failCount}. Check errors.`, {
        position: "top-right",
        autoClose: 5000,
      });
      setScannedUsers([]);
    } else {
      toast.error(`Failed to submit all ${failCount} user(s)`, {
        position: "top-right",
        autoClose: 5000,
      });
    }

    if (errors.length > 0) {
      console.error('Submission errors:', errors);
    }
  };

  // Handler for confirmation modal OK button
  const handleConfirmationOk = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
    setParsedQRData(null);
    setScannedData('');
    setAssignedVehicleFromQR('');
    setIsProcessing(false);
    lastScanRef.current = '';

    toast.info('Ready for next scan', {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
    });
  };

  const handleScanError = (errorMessage) => {
    if (!errorMessage) return;
    const msg = typeof errorMessage === 'string' ? errorMessage : (errorMessage?.message || 'Unknown scan error');

    // Ignore benign decode errors that occur when no QR is in view
    if (
      msg.includes('No MultiFormat Readers') ||
      msg.toLowerCase().includes('notfoundexception') ||
      msg.toLowerCase().includes('no barcode found') ||
      msg.toLowerCase().includes('no decipherable code')
    ) {
      return;
    }

    console.log('Scan error:', msg);
    setDebugInfo(`Error: ${msg}`);
    if (msg.toLowerCase().includes('permission') ||
        msg.toLowerCase().includes('not found') ||
        msg.toLowerCase().includes('camera') ||
        msg.toLowerCase().includes('access') ||
        msg.toLowerCase().includes('notallowederror') ||
        msg.toLowerCase().includes('notreadableerror')) {
      setError(`Camera access error: ${msg}`);
    }
  };

  const startScanner = () => {
    console.log('Starting scanner...');
    setDebugInfo('Starting scanner...');
    setScannerActive(true);
    setScannedData('');
    setError('');
    setIsProcessing(false);
    lastScanRef.current = '';

    // Show info notification
    toast.info('📷 Scanner activated. Position QR code in camera view.', {
      position: "top-right",
      autoClose: 2500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const stopScanner = () => {
    console.log('Stopping scanner...');
    setDebugInfo('Stopping scanner...');
    setScannerActive(false);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userPhoneNumber');
    window.location.href = '/signin';
  };

  if (!isOtpVerified) {
    return (
      <div className="driver-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Verifying authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-container">
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
      </div>

      <div className="driver-content">
        <div className="driver-header">
          <div className="header-left">
            <div className="logo">
              <div className="logo-rectangles">
                <div className="rect rect-1"></div>
                <div className="rect rect-2"></div>
              </div>
            </div>
            <h1>Gatemen Dashboard</h1>
          </div>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>

        <div className="driver-main">
          {/* Selection Section - Only visible when both dropdowns are NOT selected */}
          {!(selectedVehicle && selectedRound) && (
            <div className="history-section">
              <div className="history-card">
                <div className="history-header">
                  <h2>Select Details</h2>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginTop: '8px' }}>
                    Please select both vehicle and route to proceed with QR code scanning
                  </p>
                </div>
                <div className="history-list">
                  <div className="history-item">
                    <label htmlFor="vehicle-select" className="history-data" style={{ display: 'block', marginBottom: '8px' }}>
                      <strong>Select Vehicle</strong>
                    </label>
                    <select
                      id="vehicle-select"
                      value={selectedVehicle}
                      onChange={(e) => setSelectedVehicle(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                    >
                      <option value="" style={{ color: '#000' }}>
                        {isVehiclesLoading ? 'Loading vehicles...' : 'Select vehicle'}
                      </option>
                      {vehicles && vehicles.length > 0 && vehicles.map((v) => (
                        <option key={v.id || v.registrationNumber} value={v.registrationNumber} style={{ color: '#000' }}>
                          {v.registrationNumber}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="history-item" style={{ marginTop: '12px' }}>
                    <label htmlFor="round-select" className="history-data" style={{ display: 'block', marginBottom: '8px' }}>
                      <strong>Select Route</strong>
                    </label>
                    <select
                      id="round-select"
                      value={selectedRound}
                      onChange={(e) => setSelectedRound(e.target.value)}
                      disabled={!selectedVehicle || isRoutesLoading}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                    >
                      <option value="" style={{ color: '#000' }}>
                        {!selectedVehicle
                          ? 'Select a vehicle first'
                          : isRoutesLoading
                            ? 'Loading routes...'
                            : routes.length === 0
                              ? 'No routes available'
                              : 'Select route'}
                      </option>
                      {routes.map((route) => (
                        <option key={route.id} value={route.round} style={{ color: '#000' }}>
                          {route.round} - {route.timing} ({route.source} → {route.destination})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* QR Scanner Section - Only show when both selections are made */}
          {selectedVehicle && selectedRound && (
            <div className="scanner-section">
              <div className="scanner-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2>QR Code Scanner</h2>
                  <button 
                    onClick={() => {
                      setSelectedVehicle('');
                      setSelectedRound('');
                      setScannerActive(false);
                      setScannedData('');
                      setParsedQRData(null);
                      setAssignedVehicleFromQR('');
                      setError('');
                      setDebugInfo('');
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      color: '#fff',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Change Selection
                  </button>
                </div>
                
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  background: 'rgba(34, 197, 94, 0.1)', 
                  border: '1px solid rgba(34, 197, 94, 0.3)', 
                  borderRadius: '8px',
                  color: '#22c55e',
                  fontSize: '14px'
                }}>
                  ✓ Selected: <strong>{selectedVehicle}</strong> | Route: <strong>{selectedRound}</strong>
                </div>

                {/* Debug info */}
                {debugInfo && (
                  <div className="debug-info" style={{ 
                    background: '#f0f0f0', 
                    padding: '10px', 
                    margin: '10px 0', 
                    fontSize: '12px',
                    borderRadius: '4px'
                  }}>
                    <strong>Debug:</strong> {debugInfo}
                  </div>
                )}

                {!scannerActive ? (
                  <div className="scanner-placeholder">
                    <div className="scanner-icon">📱</div>
                    <p>Click the button below to start scanning QR codes</p>
                    <button onClick={startScanner} className="start-scan-btn">Start Scanner</button>
                  </div>
                ) : (
                  <div className="scanner-active">
                    {isProcessing && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(34, 197, 94, 0.95)',
                        color: 'white',
                        padding: '20px 30px',
                        borderRadius: '12px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        zIndex: 1000,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '3px' }}></div>
                        Processing QR Code...
                      </div>
                    )}
                    <div className="scanner-container">
                    <QrScanner
                        ref={scannerRef}
                        onUpdate={(err, result) => {
                          if (err) {
                            // Ignore normal decode misses and surface meaningful errors only
                            const m = typeof err === 'string' ? err : (err?.message || '');
                            if (
                              m.includes('No MultiFormat Readers') ||
                              m.toLowerCase().includes('notfoundexception')
                            ) {
                              return;
                            }
                            handleScanError(err);
                            return;
                          }
                          if (result && !isProcessing) {
                            const text = result?.text || result?.rawValue || '';
                            handleScan(text, result);
                          }
                        }}
                        constraints={{
                          facingMode: { ideal: 'environment' },
                          aspectRatio: { ideal: 1.7777777778 },
                          width: { ideal: 1920 },
                          height: { ideal: 1080 },
                          focusMode: 'continuous',
                          frameRate: { ideal: 60, min: 30 },
                          advanced: [{ torch: false }]
                        }}
                        style={{ width: '100%' }}
                        delay={30}
                      />
                    </div>
                    <button onClick={stopScanner} className="stop-scan-btn">Stop Scanner</button>
                  </div>
                )}

                {error && <div className="error-message">{error}</div>}

                {parsedQRData && (
                  <div className="parsed-data-section">
                    <h3>Last Scanned:</h3>
                    <div className="parsed-data">
                      <div className="data-row">
                        <strong>Name:</strong> {parsedQRData.firstName} {parsedQRData.secondName}
                      </div>
                      <div className="data-row">
                        <strong>Employee ID:</strong> {parsedQRData.empId}
                      </div>
                      <div className="data-row">
                        <strong>Status:</strong> {parsedQRData.status}
                      </div>
                      <div className="data-row assigned-vehicle-row">
                        <strong>Assigned Vehicle:</strong> 
                        <span className={`assigned-vehicle ${assignedVehicleFromQR === selectedVehicle ? 'match' : 'no-match'}`}>
                          {assignedVehicleFromQR || 'Not assigned'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Submit Details Section - Show scanned users list and submit button */}
        {selectedVehicle && selectedRound && (
          <div className="history-section">
            <div className="history-card">
              <div className="history-header">
                <h2>Submit Details</h2>
                {scannedUsers.length > 0 && (
                  <button
                    onClick={clearAllScannedUsers}
                    className="clear-history-btn"
                    style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                      color: '#ef4444'
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div style={{
                marginBottom: '16px',
                padding: '10px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.7)'
              }}>
                Vehicle: <strong style={{ color: '#22c55e' }}>{selectedVehicle}</strong> |
                Route: <strong style={{ color: '#22c55e' }}>{selectedRound}</strong> |
                Scanned: <strong style={{ color: '#f97316' }}>{scannedUsers.length}</strong>
              </div>

              <div className="history-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {scannedUsers.length === 0 ? (
                  <div className="no-history" style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                    <p>No users scanned yet</p>
                    <p style={{ fontSize: '12px', marginTop: '8px' }}>Scan QR codes to add users to the list</p>
                  </div>
                ) : (
                  scannedUsers.map((user, index) => (
                    <div
                      key={user.empId}
                      className="history-item"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        marginBottom: '8px',
                        background: user.vehicleMatch
                          ? 'rgba(34, 197, 94, 0.1)'
                          : 'rgba(249, 115, 22, 0.1)',
                        border: user.vehicleMatch
                          ? '1px solid rgba(34, 197, 94, 0.3)'
                          : '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: '#fff',
                          fontWeight: '600',
                          marginBottom: '4px'
                        }}>
                          {index + 1}. {user.firstName} {user.secondName}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: 'rgba(255,255,255,0.7)',
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <span>ID: {user.empId}</span>
                          <span>Assigned: {user.assignedVehicle || 'N/A'}</span>
                          <span style={{
                            color: user.vehicleMatch ? '#22c55e' : '#f97316'
                          }}>
                            {user.vehicleMatch ? '✓ Match' : '⚠ Mismatch'}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: 'rgba(255,255,255,0.5)',
                          marginTop: '4px'
                        }}>
                          Scanned at: {user.scannedAt}
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromScannedUsers(user.empId)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          color: '#ef4444',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginLeft: '12px'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Submit Button */}
              <div style={{ marginTop: '20px' }}>
                <button
                  className="submit-btn"
                  onClick={submitAllUsers}
                  disabled={scannedUsers.length === 0 || isSubmitting}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: scannedUsers.length === 0
                      ? 'rgba(128, 128, 128, 0.5)'
                      : 'linear-gradient(135deg, #4CAF50, #45a049)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: scannedUsers.length === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: scannedUsers.length > 0 ? '0 4px 15px rgba(76, 175, 80, 0.3)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                >
                  {isSubmitting && (
                    <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }}></div>
                  )}
                  {isSubmitting
                    ? 'Submitting...'
                    : `Submit ${scannedUsers.length} User${scannedUsers.length !== 1 ? 's' : ''}`
                  }
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
      
      {/* Confirmation Modal for Inactive Users */}
      {showConfirmation && confirmationData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            color: 'white'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '24px' }}>
              ⚠️ User Status: {confirmationData.status}
            </h2>
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Name:</strong> {confirmationData.firstName} {confirmationData.secondName}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Employee ID:</strong> {confirmationData.empId}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Email:</strong> {confirmationData.email}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Department:</strong> {confirmationData.department}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Assigned Vehicle:</strong> {confirmationData.assignedVehicle || 'Not assigned'}
              </div>
              {confirmationData.assignedVehicle &&
               confirmationData.assignedVehicle !== selectedVehicle && (
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  background: 'rgba(255, 152, 0, 0.2)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 152, 0, 0.5)'
                }}>
                  ⚠️ Vehicle Mismatch: User's assigned vehicle is different from selected vehicle
                </div>
              )}
            </div>
            <button
              onClick={handleConfirmationOk}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.9)',
                color: '#667eea',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseOver={(e) => e.target.style.background = 'white'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.9)'}
            >
              OK - Ready for Next Scan
            </button>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default Driver;
